import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import styled, { keyframes, css } from "styled-components";
import {
  LayoutDashboard,
  BarChart3,
  Users,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  X,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  CheckCircle2,
  Sparkles,
  TrendingUp,
  PieChart,
  ShieldCheck,
  Search,
} from "lucide-react";

/**
 * DashboardOverview
 * 2-column rows: text card (left) + dedicated gallery (right).
 *
 * Galleries buckets:
 *  - dash:        only dashboard*.png
 *  - raport:      only raport*.png
 *  - kontrahent:  only raport_kontr*.png
 *
 * Props:
 *  - accent?: string
 *  - galleries?: { dash?: string[], raport?: string[], kontrahent?: string[] }
 */
export default function DashboardOverview({ accent = "#3bc3cc", galleries = {} }) {
  const {
    dash: dashImages,
    raport: raportImages,
    kontrahent: kontrImages,
  } = useMemo(() => {
    const defDash = ["/dashboard1.png", "/dashboard2.png"]; // dashboard*
    const defRaport = ["/raport1.png", "/raporty5.png", "/raporty6.png"]; // raport*
    const defKontr = [
      "/raport_kontr.png",
      "/raport_kontr1.png",
      "/raport_kontr2.png",
      "/raport_kontr3.png",
    ]; // raport_kontr*
    return {
      dash: galleries.dash && galleries.dash.length ? galleries.dash : defDash,
      raport:
        galleries.raport && galleries.raport.length
          ? galleries.raport
          : defRaport,
      kontrahent:
        galleries.kontrahent && galleries.kontrahent.length
          ? galleries.kontrahent
          : defKontr,
    };
  }, [galleries]);

  return (
    <Section>
      <Wrap>
        <Header>
          <h2>Zobacz, jak to działa</h2>
        </Header>

        {/* ROW 1 — Dashboard (dash only) */}
        <Row>
          <FeatCard $accent={accent}>
            <CardHead>
              <IconWrap $accent={accent}>
                <LayoutDashboard size={28} />
              </IconWrap>
              <CardKicker>Dashboard</CardKicker>
            </CardHead>

            {/* Title converted to bullet */}
            <CardTextBlock>
              <BulletP icon={Sparkles} accent={accent}>
                <strong>Zobacz swoją firmę w nowym świetle.</strong>
              </BulletP>

              <BulletP icon={CheckCircle2} accent={accent}>
                <strong>
                  Wskaźniki w czytelnych wykresach i kaflach. Błyskawiczny podgląd kondycji firmy.
                </strong>
              </BulletP>
              <BulletP icon={Sparkles} accent={accent}>
                <strong>Podejmuj decyzje szybciej, mając pełny obraz sytuacji.</strong>
              </BulletP>
            </CardTextBlock>
          </FeatCard>

          <GalleryCard>
            <CarouselGallery images={dashImages} accent={accent} />
          </GalleryCard>
        </Row>

        {/* ROW 2 — Raport (raport only) */}
        <Row>
          
          <FeatCard $accent={accent}>
            <CardHead>
              <IconWrap $accent={accent}>
                <BarChart3 size={28} />
              </IconWrap>
              <CardKicker>Raporty sprzedaży i zakupu</CardKicker>
            </CardHead>

            {/* Title converted to bullet */}
            <CardTextBlock>
              <BulletP icon={PieChart} accent={accent}>
                <strong>Przepływy finansowe pod pełną kontrolą</strong>
              </BulletP>

              <BulletP icon={PieChart} accent={accent}>
                <strong>
                  Przychody, koszty, marże — w ujęciu kontrahenta, regionu i kategorii.
                </strong>
              </BulletP>
              <BulletP icon={TrendingUp} accent={accent}>
                <strong>
                  Zobacz, kto generuje największe wpływy i gdzie rosną koszty.
                </strong>
              </BulletP>
            </CardTextBlock>
          </FeatCard>

          <GalleryCard>
            <CarouselGallery images={raportImages} accent={accent} />
          </GalleryCard>
        </Row>

        {/* ROW 3 — Raport Kontrahent (kontr only) */}
        <Row>
          <FeatCard $accent={accent}>
            <CardHead>
              <IconWrap $accent={accent}>
                <Users size={28} />
              </IconWrap>
              <CardKicker>RAPORT WIARYGODNOŚCI KONTRAHENTA, LISTY SANKCYJNE I AML</CardKicker>
            </CardHead>

            {/* Title converted to bullet */}
            <CardTextBlock>
              <BulletP icon={ShieldCheck} accent={accent}>
                <strong>Ocena ryzyka i wiarygodności partnerów</strong>
              </BulletP>

              <BulletP icon={ShieldCheck} accent={accent}>
                <strong>
                  Błyskawiczny wgląd w wiarygodność firmy, długi publiczne, zmiany w zarządzie.
                </strong>
              </BulletP>
              <BulletP icon={Search} accent={accent}>
                <strong>
                  Weryfikacja AML i KYC członków zarządu na listach sankcyjnych, informacje o beneficjentach rzeczywistych i PEP, KNF, UOKiK
                </strong>
              </BulletP>
            </CardTextBlock>
          </FeatCard>

          <GalleryCard>
            <CarouselGallery images={kontrImages} accent={accent} />
          </GalleryCard>
        </Row>
      </Wrap>
    </Section>
  );
}

/* ================= Bullet component ================= */

function BulletP({ children, icon: Icon = CheckCircle2, accent = "#3bc3cc" }) {
  return (
    <BulletLine $accent={accent}>
      <Icon size={22} />   {/* was 18 */}
      <span>{children}</span>
    </BulletLine>
  );
}

/* ================= CarouselGallery ================= */

function CarouselGallery({ images = [], accent = "#3bc3cc" }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  // zoom & pan
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  // carousel paging — show exactly 1 per view
  const [carouselPage, setCarouselPage] = useState(0);
  const perView = 1;
  const maxCarouselPage = Math.max(0, Math.ceil(images.length / perView) - 1);

  const resetZoom = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  const openAt = useCallback(
    (i) => {
      setIdx(i);
      setOpen(true);
      resetZoom();
    },
    [resetZoom]
  );

  const prev = useCallback(() => {
    setIdx((i) => (i - 1 + images.length) % images.length);
    resetZoom();
  }, [images.length, resetZoom]);

  const next = useCallback(() => {
    setIdx((i) => (i + 1) % images.length);
    resetZoom();
  }, [images.length, resetZoom]);

  // keyboard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "+") setScale((s) => Math.min(5, s + 0.25));
      if (e.key === "-") setScale((s) => Math.max(1, s - 0.25));
      if (e.key.toLowerCase() === "r") resetZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next, resetZoom]);

  // drag to pan (when zoomed)
  const onMouseDown = (e) => {
    if (scale === 1) return;
    setDragging(true);
    startRef.current = { x: e.clientX, y: e.clientY, tx, ty };
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    setTx(startRef.current.tx + dx);
    setTy(startRef.current.ty + dy);
  };
  const onMouseUp = () => setDragging(false);

  // touch swipe/pan
  const touchRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, tx, ty };
  };
  const onTouchMove = (e) => {
    const t = e.touches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (scale > 1) {
      setTx(touchRef.current.tx + dx);
      setTy(touchRef.current.ty + dy);
    }
  };
  const onTouchEnd = (e) => {
    if (scale === 1 && e.changedTouches && e.changedTouches[0]) {
      const dx = e.changedTouches[0].clientX - touchRef.current.x;
      if (dx > 60) prev();
      else if (dx < -60) next();
    }
  };

  // wheel zoom
  const onWheel = (e) => {
    if (!open) return;
    if (e.ctrlKey || Math.abs(e.deltaY) > 30) {
      e.preventDefault();
      setScale((s) => {
        const ns = e.deltaY > 0 ? Math.max(1, s - 0.2) : Math.min(5, s + 0.2);
        if (ns === 1) {
          setTx(0);
          setTy(0);
        }
        return ns;
      });
    }
  };

  const onDoubleClick = () => setScale((s) => (s === 1 ? 2 : 1));

  return (
    <GallerySection>
      <Carousel>
        <NavBtn
          onClick={() => setCarouselPage((p) => Math.max(0, p - 1))}
          aria-label="Poprzednie"
        >
          <ChevronLeft size={22} />
        </NavBtn>

        <Viewport>
          <Track style={{ transform: `translateX(-${carouselPage * 100}%)` }}>
            {images.map((src, i) => (
              <Slide
                key={`${src}-${i}`}
                onClick={() => openAt(i)}
                title="Powiększ"
              >
                <img src={src} alt={`Zrzut ${i + 1}`} loading="lazy" />
              </Slide>
            ))}
          </Track>
        </Viewport>

        <NavBtn
          onClick={() =>
            setCarouselPage((p) => Math.min(maxCarouselPage, p + 1))
          }
          aria-label="Następne"
        >
          <ChevronRightIcon size={22} />
        </NavBtn>
      </Carousel>

      <Dots>
        {Array.from({ length: maxCarouselPage + 1 }).map((_, d) => (
          <Dot
            key={d}
            $active={d === carouselPage}
            onClick={() => setCarouselPage(d)}
            aria-label={`Strona ${d + 1}`}
          />
        ))}
      </Dots>

      {open && (
        <Lightbox
          role="dialog"
          aria-modal="true"
          onWheel={onWheel}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
        >
          <Overlay onClick={() => setOpen(false)} />
          <Stage>
            <BtnNavLeft onClick={prev} aria-label="Poprzednie">
              <ChevronLeft size={22} />
            </BtnNavLeft>
            <Canvas
              onMouseDown={onMouseDown}
              onDoubleClick={onDoubleClick}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <ImageInner
                style={{
                  transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                }}
              >
                <img
                  src={images[idx]}
                  alt={`Podgląd ${idx + 1}`}
                  draggable={false}
                />
              </ImageInner>
            </Canvas>
            <BtnNavRight onClick={next} aria-label="Następne">
              <ChevronRightIcon size={22} />
            </BtnNavRight>

            <Toolbar>
              <TButton
                onClick={() => setScale((s) => Math.max(1, s - 0.25))}
                aria-label="Pomniejsz"
              >
                <ZoomOut size={22} />
              </TButton>
              <TButton
                onClick={() => setScale((s) => Math.min(5, s + 0.25))}
                aria-label="Powiększ"
              >
                <ZoomIn size={22} />
              </TButton>
              <TButton onClick={resetZoom} aria-label="Reset">
                <RotateCcw size={22} />
              </TButton>
              <Counter>
                {idx + 1} / {images.length}
              </Counter>
              <Close onClick={() => setOpen(false)} aria-label="Zamknij">
                <X size={22} />
              </Close>
            </Toolbar>
          </Stage>
        </Lightbox>
      )}
    </GallerySection>
  );
}

/* ================= styles ================= */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Section = styled.section`
  position: relative;
  padding: 2rem;
  background: url("/back.jpg") no-repeat center center fixed;
  background-size: cover;
  overflow: hidden;
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease both;
`;

const Header = styled.div`
  max-width: 1100px;
  margin: 0 auto 1.75rem auto;
  text-align: center;
  background: transparent;

  h2 {
    font-size: 2.35rem;
    line-height: 1.2;
    margin: 0 0 0.6rem;
    color: #1f2937;
  }
  p {
    color: #475569;
    font-size: 1.05rem;
    margin: 0 auto;
    max-width: 900px;
  }
`;

/* === Two-column rows === */
const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: stretch;
  margin-bottom: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const cardBase = css`
  border-radius: 16px;
  padding: 1rem;
  position: relative;
`;

/* Left column text card */
const FeatCard = styled.article`
  ${cardBase};
  &::before {
    content: "";
    position: absolute;
    inset: -1px auto auto -1px;
    width: calc(100% + 2px);
    height: 4px;
    border-radius: 16px 16px 0 0;
    background: ${({ $accent }) => $accent || "#3bc3cc"};
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  justify-content: flex-start;
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  color: #0f172a;
  background: ${({ $accent }) => gradientOf($accent || "#3bc3cc")};
  border: 1px solid ${({ $accent }) => hexAlpha($accent || "#3bc3cc", 0.35)};
`;

const CardKicker = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  font-size: 0.9rem;
  color: #475569;
  
`;

const CardTextBlock = styled.div`
  margin: 0.25rem 0;
  color: #55627a;
  font-size: 1.25rem;
  line-height: 1.6;
  text-align: left;
  
`;

const BulletLine = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;               /* more space between icon and text */
  margin: 0.75rem 0;         /* more space between bullets */
  font-size: clamp(1.15rem, 1.1vw + 1rem, 1.2rem); /* bigger, responsive */
  line-height: 1.75;
  letter-spacing: 0.01em;
    margin-top: 1.5rem;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: ${({ $accent }) => $accent || "#3bc3cc"};
  }

  span {
    color: #0f172a;
  }
`;


/* Right column gallery card */
const GalleryCard = styled.aside`
  ${cardBase};
  display: flex;
  flex-direction: column;
`;

/* ===== Gallery styles ===== */
const GallerySection = styled.section`
  display: block;
`;

const Carousel = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  gap: 0.5rem;
`;

const Viewport = styled.div`
  overflow: hidden;
  border-radius: 12px;
`;

const Track = styled.div`
  display: flex;
  transition: transform 0.35s ease;
`;

/* show exactly one visible slide */
const Slide = styled.button`
  flex: 0 0 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: zoom-in;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(2, 8, 23, 0.06);

  img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    display: block;
    transition: transform 0.25s ease;
  }
  &:hover img {
    transform: scale(1.03);
  }
`;

const NavBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(2, 8, 23, 0.06);
  &:hover {
    background: #f8fafc;
  }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.6rem;
`;
const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#3bc3cc" : "#d1d5db")};
`;

/* ===== Lightbox ===== */
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
`;
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(2, 8, 23, 0.82);
`;
const Stage = styled.div`
  position: relative;
  width: min(1100px, 94vw);
  height: min(82vh, 820px);
  display: grid;
  place-items: center;
`;
const Canvas = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  overflow: hidden;
  cursor: grab;
`;
const ImageInner = styled.div`
  transition: transform 0.05s linear;
  will-change: transform;
  img {
    max-width: 100%;
    max-height: 100%;
    user-select: none;
    pointer-events: none;
    display: block;
    border-radius: 8px;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
  }
`;
const navBtnBase = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  backdrop-filter: blur(2px);
`;
const BtnNavLeft = styled.button`
  ${navBtnBase};
  left: -56px;
  @media (max-width: 900px) {
    left: 8px;
  }
`;
const BtnNavRight = styled.button`
  ${navBtnBase};
  right: -56px;
  @media (max-width: 900px) {
    right: 8px;
  }
`;
const Toolbar = styled.div`
  position: absolute;
  bottom: -52px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 900px) {
    bottom: 8px;
  }
`;
const TButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
`;
const Close = styled(TButton)`
  position: absolute;
  right: -52px;
  top: -52px;
  @media (max-width: 900px) {
    right: 8px;
    top: 8px;
  }
`;
const Counter = styled.span`
  color: #e5e7eb;
  font-size: 0.9rem;
  margin-left: 0.25rem;
`;

/* ===== helpers ===== */
function hexAlpha(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
function gradientOf(hex) {
  return `linear-gradient(135deg, #ffffff 0%, ${hex} 100%)`;
}
